import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import StarRating from '../StarRating/StarRating';
import { DESIGN_TOKENS } from '../../../styles/constants';

interface ReviewItemProps {
  date: string;
  rating: number;
  userName: string;
  orderId: string;
  reviewText: string;
  photos?: string[];
  reply?: {
    text: string;
  };
  reviewId?: string;
  onReply?: () => void;
  onEditReply?: () => void;
  onPhotoClick?: (photoUrl: string) => void;
}

const ReviewItem: React.FC<ReviewItemProps> = ({
  date,
  rating,
  userName,
  orderId,
  reviewText,
  photos = [],
  reply,
  reviewId,
  onReply,
  onEditReply,
  onPhotoClick,
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: '40px',
        padding: '24px 0',
        borderBottom: '1px solid #F1F4F4',
        alignSelf: 'stretch',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: '16px',
          flex: 1,
        }}
      >
        {/* Header with date, rating, and user info */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <Typography
              sx={{
                fontFamily: DESIGN_TOKENS.typography.fontFamily,
                fontSize: '13px',
                fontWeight: 400,
                lineHeight: '18.2px',
                letterSpacing: '-0.08px',
                color: '#93989C',
              }}
            >
              {date}
            </Typography>
            {reviewId && (
              <Typography
                sx={{
                  fontFamily: DESIGN_TOKENS.typography.fontFamily,
                  fontSize: '11px',
                  fontWeight: 400,
                  lineHeight: '15.4px',
                  color: '#93989C',
                  opacity: 0.7,
                }}
              >
                ID: {reviewId}
              </Typography>
            )}
          </Box>
          
          <StarRating rating={rating} showNumber={true} size="small" />
          
          <Box sx={{ display: 'flex', gap: '4px' }}>
            <Typography
              sx={{
                fontFamily: DESIGN_TOKENS.typography.fontFamily,
                fontSize: '13px',
                fontWeight: 400,
                lineHeight: '18.2px',
                letterSpacing: '-0.08px',
                color: '#181818',
              }}
            >
              {userName},
            </Typography>
            <Typography
              sx={{
                fontFamily: DESIGN_TOKENS.typography.fontFamily,
                fontSize: '13px',
                fontWeight: 400,
                lineHeight: '18.2px',
                letterSpacing: '-0.08px',
                color: '#181818',
              }}
            >
              Order {orderId}
            </Typography>
          </Box>
        </Box>

        {/* Photos */}
        {photos.length > 0 && (
          <Box sx={{ display: 'flex', gap: '8px' }}>
            {photos.map((photo, index) => (
              <Box
                key={index}
                onClick={() => onPhotoClick?.(photo)}
                sx={{
                  width: '80px',
                  height: '80px',
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #C5CBD3',
                  borderRadius: '12px',
                  padding: '4px',
                  cursor: 'pointer',
                  '&:hover': {
                    opacity: 0.8,
                  },
                }}
              >
                <Box
                  component="img"
                  src={photo}
                  alt={`Review photo ${index + 1}`}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '8px',
                  }}
                />
              </Box>
            ))}
          </Box>
        )}

        {/* Review text */}
        <Typography
          sx={{
            fontFamily: DESIGN_TOKENS.typography.fontFamily,
            fontSize: '16px',
            fontWeight: 400,
            lineHeight: '22.4px',
            color: '#181818',
          }}
        >
          {reviewText}
        </Typography>

        {/* Reply section */}
        {reply && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
            }}
          >
            <Typography
              sx={{
                fontFamily: DESIGN_TOKENS.typography.fontFamily,
                fontSize: '13px',
                fontWeight: 400,
                lineHeight: '18.2px',
                letterSpacing: '-0.08px',
                color: '#93989C',
              }}
            >
              Your reply
            </Typography>
            <Typography
              sx={{
                fontFamily: DESIGN_TOKENS.typography.fontFamily,
                fontSize: '16px',
                fontWeight: 400,
                lineHeight: '22.4px',
                color: '#181818',
              }}
            >
              {reply.text}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Action button */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {reply ? (
          <Button
            onClick={onEditReply}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '8px',
              padding: '2px 32px 0px',
              width: '160px',
              height: '56px',
              border: '1px solid #67314F',
              borderRadius: '16px',
              fontFamily: DESIGN_TOKENS.typography.fontFamily,
              fontSize: '16px',
              fontWeight: 700,
              lineHeight: '24px',
              letterSpacing: '-0.32px',
              color: '#67314F',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'rgba(103, 49, 79, 0.04)',
                border: '1px solid #67314F',
              },
            }}
          >
            Edit reply
          </Button>
        ) : (
          <Button
            onClick={onReply}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '8px',
              padding: '2px 32px 0px',
              width: '160px',
              height: '56px',
              backgroundColor: '#67314F',
              borderRadius: '16px',
              fontFamily: DESIGN_TOKENS.typography.fontFamily,
              fontSize: '16px',
              fontWeight: 700,
              lineHeight: '24px',
              letterSpacing: '-0.32px',
              color: '#FFFFFF',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#5a2a42',
              },
            }}
          >
            Reply
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default ReviewItem;


